import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { LinkComponent } from '../../atoms/link/link.component';
import TeaserType from '../../../shared/enums/teaser.type.enums';

import { BadgeComponent } from '../../atoms/badge/badge.component';
import { ImageComponent } from '../../atoms/image/image.component';
import ImageType from '../../../shared/enums/image-type.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';

import { IconComponent } from '../../atoms/icon/icon.component';

import { ButtonType } from '../../../shared/enums/button-type.enums';
import LinkType from '../../../shared/enums/link-type.enums';

import { Colors, Headlines, Sizes } from '../../../shared/enums/shared.enums';

import { Router } from '@angular/router';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { Video } from '../../../shared/interfaces/video.interface';
import { Link } from '../../../shared/interfaces/link.interface';
import { ActionType } from '../../../shared/enums/action-type.enums';
import {
  CaasComponents,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { TagsService } from '../../../services/tags.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
@Component({
  selector: 'hdi-teaser',
  standalone: true,
  templateUrl: './teaser.component.html',
  styleUrls: ['./teaser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LinkComponent,
    BadgeComponent,
    ImageComponent,
    IconComponent,
    CapitalizePipe,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class TeaserComponent implements OnChanges {
  @Input() caasData?: CaasComponents['value'][number];
  @Input() disabled: boolean = false;
  @Input() type?: TeaserType = TeaserType.SMALL;
  @Input() badges?: { type: BadgeType; label: string }[];
  @Input() miniBadges?: string[] | string;
  @Input() badgeSize: Sizes = Sizes.XMEDIUM;
  @Input() headline?: string = '';
  @Input() date?: string;
  @Input() description?: string;
  @Input() CTA: ActionType = ActionType.LINK;
  TeaserType = TeaserType;
  @Input() badgeSubline?: string;
  @Input() mediaType: 'image' | 'icon' = 'image';
  /* types props */
  @Input() buttonType?: ButtonType = ButtonType.SECONDARY;
  @Input() linkType?: LinkType;
  @Input() iconButton: string = 'arrow-right';
  @Input() href?: string;
  @Input() fragment: string = '';
  @Input() hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() target?: string;
  @Input() title: string = '';
  @Input() label?: string;
  @Input() labelArray?: string[];
  @Input() headlineSize?: Headlines = Headlines.H4;
  @Input() descriptionSize?: Sizes = Sizes.SMALL;
  @Input() dropdownItems?: Link[];
  Headlines = Headlines;
  ImageType: typeof ImageType = ImageType;
  /* image props */
  @Input() imageSrc?: string;
  @Input() imageAlt?: string;
  @Input() imageTitle?: string;
  @Input() imageBorder = false;
  /* icon props */
  @Input() iconType?: string;
  @Input() iconSize?: Sizes = Sizes.LARGE;
  @Input() iconColor?: Colors = Colors.BLACK;
  /* styles props */
  @Input() border = false;
  @Input() reverse = false;
  @Input() background = Colors.NEUTRAL_LIGHTEST;
  @Input() isHidden = false;
  @Input() isMini: boolean = false;

  BgColors = Colors;
  Sizes = Sizes;
  ActionType = ActionType;
  @Input() isPadding?: boolean = true;
  @Input() video: Video | undefined;
  @Input() imageHeight?: string;
  @Input() columns?: number;
  @Input() layout?: string;
  @Input() lazy: boolean = true;

  @ViewChild('hdiLink') hdiLink: LinkComponent | undefined;
  @ViewChild('teaser') teaser: ElementRef | undefined;

  @Output() triggerLinkClick = new EventEmitter();

  constructor(
    private readonly router: Router,
    public readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly cdr: ChangeDetectorRef,
    private readonly imagesService: ImagesService,
    private readonly tagsService: TagsService,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      //if current element is hdi-link use handleLinkClick instead if handleteaserClick
      if (this.hdiLink) {
        this.handleLinkClick();
      } else if (this.teaser) {
        this.handleTeaserClick();
      }
    }
  }

  get miniBadgesArray(): string[] {
    if (!this.miniBadges) return [];
    return Array.isArray(this.miniBadges) ? this.miniBadges : [this.miniBadges];
  }

  ngOnChanges() {
    if (this.caasData) {
      if (this.caasData.formData) {
        this.setCaasContent(this.caasData);
      } else {
        console.info('Missing formData in caasData:', this.caasData);
      }
    }

    if (this.type === TeaserType.SMALL) {
      this.background = this.teaser?.nativeElement
        .closest('section')
        ?.classList.contains('bg-grey')
        ? Colors.WHITE
        : Colors.NEUTRAL_LIGHTEST;
    }
    this.cdr.detectChanges();
  }

  handleTeaserClick() {
    this.cdr.detectChanges();
    if (this.type === TeaserType.HUB) {
      this.router.navigate([this.href]);
    } else if (this.type === TeaserType.LANDSCAPE && this.hdiLink) {
      this.handleLinkType();
    }
  }

  handleButtonClick() {
    if (this.hrefType === HrefType.ANGULAR_ROUTE) {
      this.router.navigate([this.href]);
      this.document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  handleLinkClick() {
    this.triggerLinkClick.emit();
  }

  /**
   * Processes the provided caasData and sets component properties accordingly.
   * @param caasData - The data object containing teaser information.
   */
  setCaasContent(caasData: CaasComponents['value'][number]) {
    if (!caasData?.formData) {
      console.info('Teaser: Caas data or formData is undefined:', caasData);
      return;
    }

    const { formData } = caasData;

    // Set Headline
    safeAssign(
      () => {
        this.headline = formData.st_header?.value ?? '';
      },
      'Assigning headline',
      this.errorHandler
    );

    // Set Description
    safeAssign(
      () => {
        this.description = formData.st_short?.value ?? '';
      },
      'Assigning description',
      this.errorHandler
    );

    // Set Image Source and Title
    safeAssign(
      () => {
        this.imageSrc = this.setCaasImageSrc(caasData);
      },
      'Assigning imageSrc',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.imageTitle = formData.st_image_title?.value ?? '';
      },
      'Assigning imageTitle',
      this.errorHandler
    );

    // Set Image Alt Text
    safeAssign(
      () => {
        if (formData.st_image?.value?.identifier) {
          this.imageAlt =
            this.imagesService.getAltTextById(
              formData.st_image.value.identifier
            ) ?? '';
        } else {
          this.imageAlt = '';
        }
      },
      'Assigning imageAlt',
      this.errorHandler
    );

    // Set Icon Type
    safeAssign(
      () => {
        this.iconType = formData.st_icon?.value?.toString() ?? '';
      },
      'Assigning iconType',
      this.errorHandler
    );

    // Set Media Type based on Visual Type
    safeAssign(
      () => {
        this.mediaType = this.setCaasTeaserMediaType(caasData);
      },
      'Assigning mediaType',
      this.errorHandler
    );

    // Set Badges
    safeAssign(
      () => {
        this.badges = this.getBadges(
          formData.tt_tag?.value?.target?.identifier
        );
      },
      'Assigning badges',
      this.errorHandler
    );

    // Set Link Properties
    if (formData.st_link?.value?.formData) {
      const linkFormData = formData.st_link.value.formData;

      // Set Link Label and Title
      safeAssign(
        () => {
          this.label = linkFormData.lt_link_label?.value ?? '';
        },
        'Assigning label',
        this.errorHandler
      );
      safeAssign(
        () => {
          this.title = linkFormData.lt_link_title?.value ?? '';
        },
        'Assigning title',
        this.errorHandler
      );

      // Set Href and Fragment
      safeAssign(
        () => {
          if (formData?.st_link) {
            this.href = this.caasBodytextService.getHref(formData.st_link);
          }
        },
        'Assigning href',
        this.errorHandler
      );
      safeAssign(
        () => {
          if (formData?.st_link) {
            this.fragment = this.caasBodytextService.getFragment(
              formData.st_link
            );
          }
        },
        'Assigning fragment',
        this.errorHandler
      );

      // Set Href Type
      safeAssign(
        () => {
          if (formData?.st_link) {
            this.hrefType = this.caasBodytextService.getHrefType(
              formData.st_link
            );
          }
        },
        'Assigning hrefType',
        this.errorHandler
      );

      // Set Link Type
      safeAssign(
        () => {
          if (formData?.st_link) {
            this.linkType = this.caasBodytextService.getButtonLayout(
              formData.st_link
            ) as LinkType;
          }
        },
        'Assigning linkType',
        this.errorHandler
      );

      // Set Video if present
      safeAssign(
        () => {
          const { lt_link_video_type, lt_link_vimeo_id, lt_link_youtube_id } =
            linkFormData;
          this.video = this.caasBodytextService.getVideo(
            lt_link_video_type,
            lt_link_vimeo_id as CaasSimpleField,
            lt_link_youtube_id as CaasSimpleField
          );
        },
        'Assigning video',
        this.errorHandler
      );
    }

    // Set Teaser Type, Headline Size, Icon Size, and Icon Color
    safeAssign(
      () => {
        const { type, headlineSize, iconSize, iconColor } =
          this.setCaasTeaserType(caasData);
        this.type = type;
        this.headlineSize = headlineSize;
        this.iconSize = iconSize;
        this.iconColor = iconColor;
      },
      'Assigning teaser properties',
      this.errorHandler
    );
  }

  /**
   * Determines the teaser type based on layout and visual type.
   * @param caasData - The data object containing teaser information.
   * @returns An object containing type, headlineSize, iconSize, and iconColor.
   */
  private setCaasTeaserType(caasData: CaasComponents['value'][number]): {
    type: TeaserType;
    headlineSize: Headlines;
    iconSize: Sizes;
    iconColor: Colors;
  } {
    const layout = this.layout; // Use the passed layout
    const visualType = caasData.formData.st_visual_type?.value.identifier;

    if (layout === 'horizontal') {
      return visualType === 'icon'
        ? {
            type: TeaserType.STAGE,
            headlineSize: Headlines.H3,
            iconSize: Sizes.XXLARGE,
            iconColor: Colors.GREEN,
          }
        : {
            type: TeaserType.LANDSCAPE,
            headlineSize: Headlines.H4,
            iconSize: Sizes.XXLARGE,
            iconColor: Colors.GREEN,
          };
    } else if (this.columns) {
      // Vertical Layout with Columns or Image
      const columns: number = this.columns;

      switch (columns) {
        case 3:
          return {
            type: TeaserType.SMALL,
            headlineSize: Headlines.H4,
            iconSize: Sizes.XXLARGE,
            iconColor: Colors.GREEN,
          };
        case 1:
          return {
            type: TeaserType.LARGE,
            headlineSize: Headlines.H4,
            iconSize: Sizes.XXLARGE,
            iconColor: Colors.GREEN,
          };
      }
    }
    // Default to SMALL if no conditions are met
    return {
      type: TeaserType.SMALL,
      headlineSize: Headlines.H4,
      iconSize: Sizes.XXLARGE,
      iconColor: Colors.GREEN,
    };
  }

  /**
   * Determines if the teaser should be reversed based on image orientation.
   * @param caasData - The data object containing teaser information.
   * @returns A boolean indicating if the teaser is reversed.
   */
  private setCaasTeaserReverse(
    caasData: CaasComponents['value'][number]
  ): boolean {
    return caasData.formData.st_image_orientation?.value.label === 'Rechts';
  }

  /**
   * Determines the media type based on visual type.
   * @param caasData - The data object containing teaser information.
   * @returns A string indicating the media type ('image' or 'icon').
   */
  private setCaasTeaserMediaType(
    caasData: CaasComponents['value'][number]
  ): 'image' | 'icon' {
    return caasData.formData.st_visual_type?.value.label === 'Bild'
      ? 'image'
      : 'icon';
  }

  /**
   * Extracts the image source URL from caasData.
   * @param caasData - The data object containing teaser information.
   * @returns A string representing the image source URL.
   */
  private setCaasImageSrc(caasData: CaasComponents['value'][number]): string {
    let imageUrlArray: string | undefined;
    safeAssign(
      () => {
        imageUrlArray = caasData.formData.st_image?.value?.url;
      },
      'Assigning image URL',
      this.errorHandler
    );

    if (!imageUrlArray) {
      console.info('TeaserComponent: st_image.url is undefined or empty.');
      return '';
    }

    try {
      const images: {
        src: string;
        media: string;
      }[] = JSON.parse(imageUrlArray);

      if (!Array.isArray(images)) {
        console.info(
          'TeaserComponent: Parsed st_image.url is not an array:',
          images
        );
        return '';
      }

      // Check if running in the browser
      if (typeof window !== 'undefined') {
        // Find the first image that matches the current viewport media query
        const matchedImage = images.find(
          (img) => window.matchMedia(img.media).matches
        );

        if (matchedImage) {
          return matchedImage.src;
        }
      }

      // Fallback to the first image in the array
      return images[0]?.src || '';
    } catch (error) {
      console.info(
        'TeaserComponent: Failed to parse st_image.url JSON:',
        error
      );
      return '';
    }
  }

  /**
   * Handles different link types based on hrefType.
   */
  private handleLinkType() {
    if (this.hdiLink) {
      if (this.hdiLink.hrefType === HrefType.OPEN_MODAL_VIDEO) {
        this.renderer.setProperty(this.hdiLink, 'showModal', true);
      }
    }
  }

  /**
   * Retrieves badges based on the provided identifier.
   * @param identifier - The identifier for the tag.
   * @returns An array of badge objects or undefined.
   */
  private getBadges(
    identifier: string | undefined
  ): { type: BadgeType; label: string }[] | undefined {
    if (!identifier) return undefined;
    const tag = this.tagsService.getTagByIdentifier(identifier);
    if (!tag) return undefined;
    return [{ type: BadgeType.DEFAULT, label: tag?.name ?? '' }];
  }
}
